import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, Btn, Field, Input, Select, Empty } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const W_EMPTY = { scope: "central", category: "", designation: "", state: "", rate_type: "daily", amount: "", effective_from: "", effective_to: "" };

export default function WageMasters() {
  const { isSuper } = useAuth();
  const [rates, setRates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(W_EMPTY);
  const [profOpen, setProfOpen] = useState(false);

  const loadRates = () => api.get("/wage-rates").then((r) => setRates(r.data));
  const loadProfiles = () => api.get("/calc-profiles").then((r) => setProfiles(r.data));
  useEffect(() => { loadRates(); loadProfiles(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try { await api.post("/wage-rates", form); toast.success("Wage rate added"); setOpen(false); setForm(W_EMPTY); loadRates(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const RateTable = ({ scope }) => {
    const list = rates.filter((r) => r.scope === scope);
    return (
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {list.length ? (
            <table className="nk-table" data-testid={`wage-table-${scope}`}>
              <thead><tr><th>Category</th><th>Designation</th>{scope === "state" && <th>State</th>}<th>Type</th><th>Amount</th><th>Effective From</th><th>Effective To</th></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td className="font-medium">{r.category}</td><td>{r.designation || "—"}</td>
                    {scope === "state" && <td>{r.state}</td>}
                    <td className="capitalize">{r.rate_type}</td><td className="nk-num font-semibold">₹{r.amount}</td>
                    <td className="nk-num">{r.effective_from}</td><td className="nk-num">{r.effective_to || "Current"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty title={`No ${scope} wage rates`} />}
        </div>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader title="Wage Masters" subtitle="Effective-dated Central & State wage rates and calculation profiles"
        actions={isSuper && <Btn onClick={() => { setForm(W_EMPTY); setOpen(true); }} data-testid="add-wage-btn"><Plus size={16} /> Add Wage Rate</Btn>} />

      <Tabs defaultValue="central">
        <TabsList>
          <TabsTrigger value="central" data-testid="tab-central">Central Wage</TabsTrigger>
          <TabsTrigger value="state" data-testid="tab-state">State Wage</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles">Calculation Profiles</TabsTrigger>
        </TabsList>
        <TabsContent value="central" className="mt-4"><RateTable scope="central" /></TabsContent>
        <TabsContent value="state" className="mt-4"><RateTable scope="state" /></TabsContent>
        <TabsContent value="profiles" className="mt-4">
          <div className="flex justify-end mb-3">
            {isSuper && <Btn onClick={() => setProfOpen(true)} data-testid="add-profile-btn"><Plus size={16} /> New Profile</Btn>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.length ? profiles.map((p) => {
              const v = p.versions?.[0];
              return (
                <Card key={p.id} className="p-4" data-testid={`profile-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="nk-head font-semibold text-slate-900">{p.name}</div>
                    <span className="nk-overline">v{v?.version} · from {v?.effective_from}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs text-slate-600">
                    {v && Object.entries(v.rules).slice(0, 12).map(([k, val]) => (
                      <div key={k} className="flex justify-between border-b border-slate-100 py-1">
                        <span className="text-slate-400">{k.replace(/_/g, " ")}</span><span className="nk-num font-medium">{val}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            }) : <Empty title="No calculation profiles" />}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Wage Rate</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scope"><Select value={form.scope} onChange={set("scope")}><option value="central">Central</option><option value="state">State</option></Select></Field>
            <Field label="Category"><Input value={form.category} onChange={set("category")} placeholder="Unskilled" data-testid="wage-category" /></Field>
            <Field label="Designation"><Input value={form.designation} onChange={set("designation")} /></Field>
            <Field label="State"><Input value={form.state} onChange={set("state")} /></Field>
            <Field label="Rate Type"><Select value={form.rate_type} onChange={set("rate_type")}><option value="daily">Daily</option><option value="monthly">Monthly</option></Select></Field>
            <Field label="Amount"><Input type="number" value={form.amount} onChange={set("amount")} data-testid="wage-amount" /></Field>
            <Field label="Effective From"><Input type="date" value={form.effective_from} onChange={set("effective_from")} /></Field>
            <Field label="Effective To"><Input type="date" value={form.effective_to} onChange={set("effective_to")} /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={save} data-testid="save-wage-btn">Save</Btn>
          </div>
        </DialogContent>
      </Dialog>

      {profOpen && <ProfileDialog onClose={() => setProfOpen(false)} onDone={loadProfiles} />}
    </div>
  );
}

const RULE_FIELDS = [
  ["wage_basis", "Wage Basis"], ["days_divisor", "Days Divisor"], ["employee_pf_pct", "Employee PF %"],
  ["employer_pf_pct", "Employer PF %"], ["pf_wage_ceiling", "PF Ceiling"], ["employee_esi_pct", "Employee ESI %"],
  ["employer_esi_pct", "Employer ESI %"], ["esi_wage_ceiling", "ESI Ceiling"], ["professional_tax", "Professional Tax"],
  ["service_charge_pct", "Service Charge %"], ["gst_pct", "GST %"], ["rounding", "Rounding"],
];
const DEFAULTS = { wage_basis: "daily", days_divisor: "26", employee_pf_pct: "12", employer_pf_pct: "13", pf_wage_ceiling: "15000", employee_esi_pct: "0.75", employer_esi_pct: "3.25", esi_wage_ceiling: "21000", professional_tax: "200", service_charge_pct: "8", gst_pct: "18", rounding: "nearest" };

function ProfileDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [effFrom, setEffFrom] = useState("");
  const [rules, setRules] = useState(DEFAULTS);
  const [componentsText, setComponentsText] = useState("");
  const save = async () => {
    let components;
    if (componentsText.trim()) {
      try { components = JSON.parse(componentsText); }
      catch { toast.error("Components JSON is invalid"); return; }
    }
    try { await api.post("/calc-profiles", { name, effective_from: effFrom, rules, components }); toast.success("Profile created"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Calculation Profile</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Profile Name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></Field>
          <Field label="Effective From"><Input type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {RULE_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}><Input value={rules[k]} onChange={(e) => setRules({ ...rules, [k]: e.target.value })} /></Field>
          ))}
        </div>
        <div className="mt-3">
          <label className="nk-overline block mb-1.5">Formula Components (JSON — optional, leave blank for standard set)</label>
          <textarea value={componentsText} onChange={(e) => setComponentsText(e.target.value)} data-testid="components-json"
            placeholder='[{"code":"earned_wage","name":"Earned Wage","type":"EARNING","formula":"daily_rate * present_days","order":6}]'
            className="w-full h-40 border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <p className="text-xs text-slate-400 mt-1">Component types: INPUT / EARNING / DEDUCTION / EMPLOYER / BILLING / TAX. Formulas may reference other component codes and <code>daily_rate</code>. Use min(), max(), iff(cond,a,b).</p>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} data-testid="save-profile-btn">Create Profile</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
